import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { networkManager } from '../services/NetworkManager';
import { DataBuild, STATUS_COLORS, LogEntry, Definition, Environment } from '../types';
import LogViewer from '../components/LogViewer';
import ReactJson from '@microlink/react-json-view';
import { useNotification } from '../contexts/NotificationContext';
import { useModal } from '../contexts/ModalContext';

// JSON Viewer Component
interface JSONViewerProps {
  data: any;
  title?: string;
  maxHeight?: string;
  bundleName?: string;
}

const JSONViewer: React.FC<JSONViewerProps> = ({ data, title, maxHeight = "max-h-[500px]", bundleName }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {title && (
        <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <h5 className="text-sm font-medium text-gray-700">{title}</h5>
        </div>
      )}
      <div className={`p-4 overflow-auto ${maxHeight}`}>
        <ReactJson
          src={data}
          name={bundleName || "root"}
          theme="rjv-default"
          style={{ backgroundColor: 'transparent' }}
          displayDataTypes={true}
          displayObjectSize={true}
          enableClipboard={true}
          collapsed={2}
          collapseStringsAfterLength={80}
          groupArraysAfterLength={100}
          sortKeys={false}
          quotesOnKeys={true}
        />
      </div>
    </div>
  );
};

// BundlesSection Component
interface BundlesSectionProps {
  buildId: string;
  bundles: any;
  jsonBundles?: any;
  buildFormats?: string[];
}

const BundlesSection: React.FC<BundlesSectionProps> = ({ buildId, bundles, jsonBundles, buildFormats }) => {
  const [decodedBundles, setDecodedBundles] = useState<Record<string, any>>({});
  const [loadingBundles, setLoadingBundles] = useState<Record<string, boolean>>({});
  const [errorBundles, setErrorBundles] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  const decodeBundle = async (bundleName: string) => {
    if (decodedBundles[bundleName]) {
      // If already decoded, just switch to this tab
      setActiveTab(bundleName);
      return;
    }

    try {
      setLoadingBundles(prev => ({ ...prev, [bundleName]: true }));
      setErrorBundles(prev => ({ ...prev, [bundleName]: '' }));
      
      const decodedData = await networkManager.decodeBundle(buildId, bundleName);
      setDecodedBundles(prev => ({ ...prev, [bundleName]: decodedData }));
      setActiveTab(bundleName);
    } catch (err: any) {
      console.error(`Error decoding bundle ${bundleName}:`, err);
      setErrorBundles(prev => ({ 
        ...prev, 
        [bundleName]: err.message || 'Failed to decode bundle' 
      }));
    } finally {
      setLoadingBundles(prev => ({ ...prev, [bundleName]: false }));
    }
  };

  const downloadBundleAsJson = (bundleName: string, data: any) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${bundleName}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyBundleUrl = async (bundleName: string, urlType: 'bin' | 'json' = 'bin') => {
    // Use the URL from the appropriate bundles object
    const bundleUrl = urlType === 'bin' ? bundles[bundleName] : jsonBundles?.[bundleName];
    if (!bundleUrl) {
      console.error(`No URL found for bundle: ${bundleName} (${urlType})`);
      return;
    }
    
    try {
      await navigator.clipboard.writeText(bundleUrl);
      setCopyFeedback(`${bundleName}-${urlType}`);
      // Clear feedback after 2 seconds
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const getBuildFormatBadges = () => {
    // Default to binary if no buildFormats provided
    const formats = buildFormats && buildFormats.length > 0 ? buildFormats : ['BINARY'];
    
    return formats.map((format) => {
      const isBinary = format.toUpperCase() === 'BINARY';
      const isJson = format.toUpperCase() === 'JSON';
      
      return (
        <span
          key={format}
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            isBinary
              ? 'bg-blue-100 text-blue-800'
              : isJson
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {format}
        </span>
      );
    });
  };

  // Convert bundles object to array if it's not already
  const bundleEntries = Array.isArray(bundles) 
    ? bundles.map((bundle, index) => ({ name: `Bundle_${index}`, data: bundle }))
    : Object.entries(bundles).map(([name, data]) => ({ name, data }));

  // Set first bundle as active tab if none is selected
  useEffect(() => {
    if (!activeTab && bundleEntries.length > 0) {
      setActiveTab(bundleEntries[0].name);
    }
  }, [bundleEntries, activeTab]);

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-xl font-semibold text-gray-900">Data Bundles</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Build formats:</span>
              <div className="flex items-center space-x-1">
                {getBuildFormatBadges()}
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            {bundleEntries.length} bundle{bundleEntries.length !== 1 ? 's' : ''} available
          </p>
        </div>
      </div>
      
      {bundleEntries.length === 0 ? (
        <div className="text-center py-8">
          <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-500">No bundles available for this build</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Bundle Tabs */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-0 overflow-x-auto">
                {bundleEntries.map(({ name, data }) => (
                  <button
                    key={name}
                    onClick={() => decodeBundle(name)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap ${
                      activeTab === name
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <span>{name}</span>
                      {loadingBundles[name] && (
                        <div className="spinner h-3 w-3 text-blue-600"></div>
                      )}
                      {decodedBundles[name] && (
                        <svg className="h-3 w-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {errorBundles[name] && (
                        <svg className="h-3 w-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </nav>
            </div>

            {/* Bundle URLs Display - Only shown when tab is clicked */}
            {activeTab && decodedBundles[activeTab] && (
              <div className="p-3 bg-gray-50 border-t border-gray-200">
                <div className="space-y-3">
                  {/* Binary Bundle URL */}
                  {bundles[activeTab] && (
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Binary Bundle (.bin):</label>
                        <div className="flex items-center space-x-2">
                          <a
                            href={bundles[activeTab]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline truncate font-mono text-sm"
                            title={bundles[activeTab]}
                          >
                            {bundles[activeTab]}
                          </a>
                          <button
                            onClick={() => copyBundleUrl(activeTab, 'bin')}
                            className={`flex-shrink-0 p-1 transition-all duration-300 ${
                              copyFeedback === `${activeTab}-bin` 
                                ? 'text-green-600 scale-110' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                            title="Copy Binary URL"
                          >
                            {copyFeedback === `${activeTab}-bin` ? (
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* JSON Bundle URL */}
                  {jsonBundles?.[activeTab] && (
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <label className="block text-xs font-medium text-gray-700 mb-1">JSON Bundle (.json):</label>
                        <div className="flex items-center space-x-2">
                          <a
                            href={jsonBundles[activeTab]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline truncate font-mono text-sm"
                            title={jsonBundles[activeTab]}
                          >
                            {jsonBundles[activeTab]}
                          </a>
                          <button
                            onClick={() => copyBundleUrl(activeTab, 'json')}
                            className={`flex-shrink-0 p-1 transition-all duration-300 ${
                              copyFeedback === `${activeTab}-json` 
                                ? 'text-green-600 scale-110' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                            title="Copy JSON URL"
                          >
                            {copyFeedback === `${activeTab}-json` ? (
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Tab Content */}
          {activeTab ? (
            <div className="space-y-4">
              {/* Error message */}
              {errorBundles[activeTab] && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  <div className="flex items-center">
                    <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errorBundles[activeTab]}
                  </div>
                </div>
              )}

              {/* Decoded bundle data */}
              {decodedBundles[activeTab] && (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Decoded Bundle Data</h5>
                  <JSONViewer 
                    data={decodedBundles[activeTab]} 
                    title="Decoded Data"
                    maxHeight="max-h-[600px]"
                    bundleName={activeTab}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500">Select a bundle tab to view its data</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DataBuildDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { showNotification } = useNotification();
  const { openModal, closeModal } = useModal();
  const [dataBuild, setDataBuild] = useState<DataBuild | null>(null);
  const [definition, setDefinition] = useState<Definition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Publish modal states
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [loadingEnvironments, setLoadingEnvironments] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<Environment | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  
  // Data for title resolution
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [dataBuilds, setDataBuilds] = useState<DataBuild[]>([]);

  useEffect(() => {
    if (id) {
      loadDataBuildDetails();
    }
  }, [id]);

  const loadDataBuildDetails = async () => {
    try {
      setLoading(true);
      const data = await networkManager.getDataBuild(id!);
      
      // Parse logs if they come as a string (backward compatibility)
      let parsedLogs: LogEntry[];
      if (typeof data.logs === 'string') {
        try {
          parsedLogs = JSON.parse(data.logs);
        } catch (parseError) {
          console.warn('Failed to parse logs as JSON, treating as plain text');
          // Convert to a single log entry if parsing fails
          parsedLogs = [{
            Message: data.logs,
            StructName: '',
            Type: 0 // INFO
          }];
        }
      } else {
        parsedLogs = data.logs as LogEntry[];
      }
      
      const buildData = {
        ...data,
        logs: parsedLogs
      };
      
      setDataBuild(buildData);
      
      // Load definition details
      try {
        const definitionData = await networkManager.getDefinition(buildData.definitionID);
        setDefinition(definitionData);
      } catch (defError) {
        console.warn('Failed to load definition details:', defError);
        // Definition loading failure is not critical, continue without it
      }
      
    } catch (err) {
      setError('Failed to load data build details');
      console.error('Error loading data build details:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadEnvironments = async () => {
    try {
      setLoadingEnvironments(true);
      const envs = await networkManager.getEnvironments();
      setEnvironments(envs);
      
      // Load definitions and data builds for title resolution
      await Promise.all([
        loadDefinitions(),
        loadDataBuilds()
      ]);
    } catch (err) {
      console.error('Error loading environments:', err);
      setPublishError('Failed to load environments');
    } finally {
      setLoadingEnvironments(false);
    }
  };

  const loadDefinitions = async () => {
    try {
      const data = await networkManager.getDefinitions();
      setDefinitions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading definitions:', err);
      setDefinitions([]);
    }
  };

  const loadDataBuilds = async () => {
    try {
      const data = await networkManager.getDataBuilds();
      const successfulDataBuilds = Array.isArray(data) 
        ? data.filter(build => build.status === 'success')
        : [];
      setDataBuilds(successfulDataBuilds);
    } catch (err) {
      console.error('Error loading data builds:', err);
      setDataBuilds([]);
    }
  };

  // Helper functions to get titles by ID
  const getDefinitionTitle = (definitionId: string) => {
    const definition = definitions.find(def => def._id === definitionId);
    return definition ? definition.title : definitionId;
  };

  const getGamedataTitle = (gamedataId: string) => {
    const gamedata = dataBuilds.find(build => build._id === gamedataId);
    return gamedata ? gamedata.title : gamedataId;
  };

  const handlePublish = async () => {
    if (!selectedEnvironment || !dataBuild) return;
    
    try {
      setPublishing(true);
      setPublishError(null);
      
      // Check if environment requires password - use ID instead of name
      // Get the actual environment ID from the environments list
      const actualEnvironment = environments.find(env => env._id === selectedEnvironment._id || env.name === selectedEnvironment.name);
      if (!actualEnvironment || !actualEnvironment._id) {
        throw new Error('Environment not found or invalid');
      }
      const environmentId = actualEnvironment._id;
      
      // Validate that we have a proper ID (not a name)
      if (!environmentId || environmentId === selectedEnvironment.name) {
        throw new Error(`Invalid environment ID. Expected ID but got: ${environmentId}`);
      }
      
      const envDetails = await networkManager.getEnvironmentDetailsById(environmentId);
      
      // Check if deploymentPassword exists and is not empty
      const hasPassword = envDetails.environmentConfigs?.deploymentPassword && 
                         envDetails.environmentConfigs.deploymentPassword.trim() !== '';
      
      if (hasPassword) {
        // Show password modal
        const PasswordModalContent = () => {
          const [password, setPassword] = useState('');
          const [validating, setValidating] = useState(false);
          
          const handlePasswordSubmit = async () => {
            if (!password.trim()) return;
            
            try {
              setValidating(true);
              
              // Attempt to update with password - use the environmentId we already validated
              await networkManager.updateEnvironment(environmentId, {
                GamedataID: dataBuild._id,
                deploymentPassword: password
              });
              
              // Success - close password modal and publish modal
              closeModal('password-required');
              setShowPublishModal(false);
              setSelectedEnvironment(null);
              setPublishError(null);
              
              showNotification(
                `Successfully published gamedata to environment: ${selectedEnvironment.name}`,
                'success',
                5000
              );
            } catch (err) {
              // Password incorrect or other error
              closeModal('password-required');
              const errorMessage = err instanceof Error ? err.message : 'Failed to publish gamedata';
              
              if (errorMessage.toLowerCase().includes('password') || errorMessage.toLowerCase().includes('incorrect')) {
                showNotification(
                  `Incorrect Password for Environment ${selectedEnvironment.name}.`,
                  'error',
                  5000
                );
              } else {
                setPublishError(errorMessage);
              }
            } finally {
              setValidating(false);
            }
          };
          
          const handleCancel = () => {
            closeModal('password-required');
          };
          
          return (
            <div className="space-y-6">
              <div>
                <label htmlFor="deployment-password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="deployment-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && password.trim() && !validating) {
                      handlePasswordSubmit();
                    }
                  }}
                  placeholder="Enter environment password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancel}
                  disabled={validating}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  CANCEL
                </button>
                <button
                  onClick={handlePasswordSubmit}
                  disabled={!password.trim() || validating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {validating ? 'Validating...' : 'OK'}
                </button>
              </div>
            </div>
          );
        };
        
        openModal({
          id: 'password-required',
          title: `Environment ${selectedEnvironment.name} Requires a Password`,
          content: <PasswordModalContent />,
          size: 'md'
        });
        
        setPublishing(false);
        return;
      }
      
      // No password required - proceed normally - use the environmentId we already validated
      await networkManager.updateEnvironment(environmentId, {
        GamedataID: dataBuild._id
      });
      
      setShowPublishModal(false);
      setSelectedEnvironment(null);
      
      // Show success notification using toast
      showNotification(
        `Successfully published gamedata to environment: ${selectedEnvironment.name}`,
        'success',
        5000
      );
    } catch (err) {
      console.error('Error publishing gamedata:', err);
      setPublishError(err instanceof Error ? err.message : 'Failed to publish gamedata');
    } finally {
      setPublishing(false);
    }
  };

  const openPublishModal = async () => {
    try {
      setPublishError(null);
      setSelectedEnvironment(null);
      setLoadingEnvironments(true);
      
      // Load environments directly and get fresh data
      const orgId = networkManager.getOrganizationId();
      if (!orgId) {
        throw new Error('Organization ID not found');
      }
      
      console.log('🔍 Loading environments for organization:', orgId);
      const envs = await networkManager.getEnvironments();
      console.log('✅ Environments loaded successfully:', envs);
      
      // Also load definitions and data builds for title resolution
      const [defs, builds] = await Promise.all([
        networkManager.getDefinitions(),
        networkManager.getDataBuilds()
      ]);
      
      const definitions = Array.isArray(defs) ? defs : [];
      const dataBuilds = Array.isArray(builds) 
        ? builds.filter(build => build.status === 'success')
        : [];
      
      // Helper functions to get titles by ID (local to this function)
      const getDefinitionTitle = (definitionId: string) => {
        const definition = definitions.find(def => def._id === definitionId);
        return definition ? definition.title : definitionId;
      };

      const getGamedataTitle = (gamedataId: string) => {
        const gamedata = dataBuilds.find(build => build._id === gamedataId);
        return gamedata ? gamedata.title : gamedataId;
      };
      
      // Create a local component that manages its own selection state
      const PublishModalContent = () => {
        const [localSelectedEnvironment, setLocalSelectedEnvironment] = useState<Environment | null>(null);
        
        const handleLocalPublish = async () => {
          if (!localSelectedEnvironment) return;
          
          try {
            setPublishing(true);
            setPublishError(null);
            
            const orgId = networkManager.getOrganizationId();
            if (!orgId) {
              throw new Error('Organization ID not found');
            }
            
            // Ensure we're using the ID, not the name
            // Get the actual environment ID from the environments list
            const actualEnvironment = envs.find(env => env._id === localSelectedEnvironment._id || env.name === localSelectedEnvironment.name);
            if (!actualEnvironment || !actualEnvironment._id) {
              throw new Error('Environment not found or invalid');
            }
            
            // Use the actual ID from the environment object
            const environmentId = actualEnvironment._id;
            
            // Validate that we have a proper ID (not a name)
            if (!environmentId || environmentId === localSelectedEnvironment.name) {
              throw new Error(`Invalid environment ID. Expected ID but got: ${environmentId}`);
            }
            
            // Check if environment requires password - use ID instead of name
            const envDetails = await networkManager.getEnvironmentDetailsById(environmentId);
            
            // Check if deploymentPassword exists and is not empty
            const hasPassword = envDetails.environmentConfigs?.deploymentPassword && 
                               envDetails.environmentConfigs.deploymentPassword.trim() !== '';
            
            if (hasPassword) {
              // Show password modal
              const PasswordModalContent = () => {
                const [password, setPassword] = useState('');
                const [validating, setValidating] = useState(false);
                
                const handlePasswordSubmit = async () => {
                  if (!password.trim()) return;
                  
                  try {
                    setValidating(true);
                    
                    // Attempt to update with password using the actual environment ID
                    await networkManager.updateEnvironment(environmentId, {
                      GamedataID: dataBuild?._id,
                      deploymentPassword: password
                    });
                    
                    // Success - close password modal and publish modal
                    closeModal('password-required');
                    closeModal('publish-gamedata');
                    setLocalSelectedEnvironment(null);
                    setPublishError(null);
                    
                    showNotification(
                      `Successfully published gamedata to environment: ${localSelectedEnvironment.name}`,
                      'success',
                      5000
                    );
                    
                    // Data updated in background, no need to reload the page
                  } catch (err) {
                    // Password incorrect or other error
                    closeModal('password-required');
                    const errorMessage = err instanceof Error ? err.message : 'Failed to publish gamedata';
                    
                    if (errorMessage.toLowerCase().includes('password') || errorMessage.toLowerCase().includes('incorrect')) {
                      showNotification(
                        `Incorrect Password for Environment ${localSelectedEnvironment.name}.`,
                        'error',
                        5000
                      );
                    } else {
                      setPublishError(errorMessage);
                      showNotification(errorMessage, 'error', 8000);
                    }
                  } finally {
                    setValidating(false);
                  }
                };
                
                const handleCancel = () => {
                  closeModal('password-required');
                };
                
                return (
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="deployment-password" className="block text-sm font-medium text-gray-700 mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        id="deployment-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && password.trim() && !validating) {
                            handlePasswordSubmit();
                          }
                        }}
                        placeholder="Enter environment password"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={handleCancel}
                        disabled={validating}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        CANCEL
                      </button>
                      <button
                        onClick={handlePasswordSubmit}
                        disabled={!password.trim() || validating}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {validating ? 'Validating...' : 'OK'}
                      </button>
                    </div>
                  </div>
                );
              };
              
              openModal({
                id: 'password-required',
                title: `Environment ${localSelectedEnvironment.name} Requires a Password`,
                content: <PasswordModalContent />,
                size: 'md'
              });
              
              setPublishing(false);
              return;
            }
            
            // No password required - proceed normally
            console.log('🚀 Publishing gamedata to environment:', localSelectedEnvironment.name);
            console.log('🔗 Backend URL:', process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api (default)');
            console.log('🔑 Environment ID being used:', environmentId);
            console.log('🔑 Environment name:', localSelectedEnvironment.name);
            
            const response = await networkManager.updateEnvironment(environmentId, {
              GamedataID: dataBuild?._id
            });
            
            console.log('✅ Environment updated successfully:', response);
            
            showNotification(`Successfully published ${dataBuild?.title || 'gamedata'} to ${localSelectedEnvironment.name}`, 'success', 5000);
            
            closeModal('publish-gamedata');
            setLocalSelectedEnvironment(null);
            setPublishError(null);
            
            // Data updated in background, no need to reload the page
            
          } catch (error) {
            console.error('❌ Error publishing gamedata:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to publish gamedata';
            setPublishError(errorMessage);
            showNotification(errorMessage, 'error', 8000);
          } finally {
            setPublishing(false);
          }
        };
        
        return (
          <div className="space-y-6">
            {publishError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {publishError}
              </div>
            )}
            
            {envs.length === 0 ? (
              <div className="text-center py-8">
                <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <p className="text-gray-500">No environments available</p>
                <p className="text-sm text-gray-400 mt-1">Create an environment first to publish gamedata</p>
              </div>
            ) : (
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Select Environment
                </label>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {envs.map((env) => (
                    <label
                      key={env._id}
                      className={`flex flex-col p-4 border rounded-lg cursor-pointer transition-colors ${
                        localSelectedEnvironment?._id === env._id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="environment"
                        value={env._id}
                        checked={localSelectedEnvironment?._id === env._id}
                        onChange={() => setLocalSelectedEnvironment(env)}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-3">
                          <div className="font-semibold text-gray-900 text-lg">{env.name}</div>
                          {localSelectedEnvironment?._id === env._id && (
                            <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        
                        {env.description && (
                          <div className="text-sm text-gray-600 mb-4">{env.description}</div>
                        )}
                        
                        <div className="space-y-3">
                          {/* Definition Section */}
                          <div className="bg-gray-50 rounded-md p-3">
                            <div className="flex items-center space-x-2 mb-2">
                              <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-sm font-medium text-gray-700">Definition</span>
                            </div>
                            {env.publishedDefinitionID ? (
                              <div className="text-sm">
                                <div className="text-gray-900 font-medium">Published</div>
                                <a 
                                  href={`/definitions/${env.publishedDefinitionID}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 hover:underline text-xs block"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {getDefinitionTitle(env.publishedDefinitionID)}
                                  <svg className="h-3 w-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                                {env.definitionPublisher && (
                                  <div className="text-gray-500 text-xs mt-1">
                                    by {env.definitionPublisher.name}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 italic">No definition published</div>
                            )}
                          </div>
                          
                          {/* Gamedata Section */}
                          <div className="bg-gray-50 rounded-md p-3">
                            <div className="flex items-center space-x-2 mb-2">
                              <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              <span className="text-sm font-medium text-gray-700">Gamedata</span>
                            </div>
                            {env.publishedDataID ? (
                              <div className="text-sm">
                                <div className="text-gray-900 font-medium">Published</div>
                                <a 
                                  href={`/data-builds/${env.publishedDataID}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 hover:underline text-xs block"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {getGamedataTitle(env.publishedDataID)}
                                  <svg className="h-4 w-4 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                                {env.dataPublisher && (
                                  <div className="text-blue-600 text-xs mt-1">
                                    by {env.dataPublisher.name}
                                  </div>
                                )}
                                {env.publishedDataID === dataBuild?._id && (
                                  <div className="text-blue-600 text-xs mt-1 font-medium">
                                    ✓ This is the current gamedata
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 italic">No gamedata published</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  closeModal('publish-gamedata');
                  setLocalSelectedEnvironment(null);
                  setPublishError(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLocalPublish}
                disabled={!localSelectedEnvironment || publishing}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {publishing ? (
                  <>
                    <div className="spinner h-4 w-4 mr-2"></div>
                    Publishing...
                  </>
                ) : (
                  'Publish'
                )}
              </button>
            </div>
          </div>
        );
      };
      
      const publishModalContent = <PublishModalContent />;
      
      // Update state with fresh data
      setEnvironments(envs);
      setDefinitions(definitions);
      setDataBuilds(dataBuilds);
      
      // Open modal with fresh content
      openModal({
        id: 'publish-gamedata',
        title: 'Publish Gamedata',
        content: publishModalContent,
        size: 'xl',
        closeOnOverlayClick: false
      });
      
    } catch (error) {
      console.error('❌ Error loading environments:', error);
      setPublishError(error instanceof Error ? error.message : 'Failed to load environments');
      
      // Show error modal
      const errorModalContent = (
        <div className="space-y-4">
          <div className="text-center py-4">
            <svg className="h-12 w-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-red-600 font-medium">Error Loading Environments</p>
            <p className="text-gray-600 text-sm mt-1">Failed to load environments. Please try again.</p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => closeModal('error-modal')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      );
      
      openModal({
        id: 'error-modal',
        title: 'Error',
        content: errorModalContent,
        size: 'md',
        closeOnOverlayClick: true
      });
    } finally {
      setLoadingEnvironments(false);
    }
  };

  const getStatusColor = (status: string) => {
    return STATUS_COLORS[status.toLowerCase() as keyof typeof STATUS_COLORS] || 'bg-gray-100 text-gray-800';
  };

  const formatExecutionTime = (time: number) => {
    if (time < 1000) return `${time}ms`;
    return `${(time / 1000).toFixed(1)}s`;
  };

  const formatDate = (dateString: string) => {
    // Parse the date string and treat it as UTC
    const date = new Date(dateString);
    
    // Get UTC components to avoid timezone conversion issues
    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1;
    const year = date.getUTCFullYear();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    
    return `${day}/${month}/${year} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading data build details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!dataBuild) {
    return (
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
        Data build not found
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 min-h-screen">
      {/* Enhanced Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-4 mb-4">
              <Link 
                to="/data-builds" 
                className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{dataBuild.title}</h1>
                <p className="text-gray-600 mt-1">Data Build Details</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-sm text-gray-600">by {dataBuild.author?.name || 'Unknown'}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-gray-600">{formatDate(dataBuild.createdAt)}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="text-sm text-gray-600">{formatExecutionTime(dataBuild.executionTime)}</span>
              </div>
            </div>
          </div>
          
          {/* Minimalist Elegant Layout */}
          <div className="flex items-center space-x-6">
            {/* Status Indicator */}
            <div className="flex items-center space-x-3">
              <div className={`w-2.5 h-2.5 rounded-full ${
                dataBuild.status === 'success' ? 'bg-green-500' :
                dataBuild.status === 'failed' ? 'bg-red-500' :
                dataBuild.status === 'running' ? 'bg-blue-500 animate-pulse' :
                'bg-gray-400'
              }`} />
              <span className={`text-sm font-medium ${
                dataBuild.status === 'success' ? 'text-green-700' :
                dataBuild.status === 'failed' ? 'text-red-700' :
                dataBuild.status === 'running' ? 'text-blue-700' :
                'text-gray-600'
              }`}>
                {dataBuild.status}
              </span>
              {dataBuild.status === 'success' && (
                <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-gray-300" />

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={openPublishModal}
                disabled={dataBuild?.status !== 'success'}
                className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  dataBuild?.status === 'success'
                    ? 'text-blue-700 hover:text-blue-800 hover:bg-blue-50 border border-blue-200 hover:border-blue-300'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                title={dataBuild?.status !== 'success' ? 'Only successful builds can be published' : 'Publish to environment'}
              >
                <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                Publish
              </button>
              
              <Link
                to={dataBuild?.status === 'success' ? `/data-comparison?gamedataB=${dataBuild._id}` : '#'}
                onClick={(e) => {
                  if (dataBuild?.status !== 'success') {
                    e.preventDefault();
                  }
                }}
                className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  dataBuild?.status === 'success'
                    ? 'text-gray-700 hover:text-gray-800 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                title={dataBuild?.status !== 'success' ? 'Only successful builds can be compared' : 'Compare with other gamedata'}
              >
                <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Compare
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Unified Build Information Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Description & Definition */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description Section */}
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900">Description</h2>
              </div>
              <p className="text-gray-700 leading-relaxed break-words overflow-wrap-anywhere">{dataBuild.description}</p>
            </div>

            {/* Definition Section */}
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900">Definition</h2>
              </div>
              {definition ? (
                <div className="space-y-2">
                  <Link 
                    to={`/definitions/${dataBuild.definitionID}`}
                    className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
                  >
                    {definition.title}
                  </Link>
                  <p className="text-sm text-gray-600">{definition.description}</p>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Link 
                    to={`/definitions/${dataBuild.definitionID}`}
                    className="text-blue-600 hover:text-blue-800 font-mono text-sm hover:underline"
                  >
                    {dataBuild.definitionID}
                  </Link>
                  <span className="text-xs text-gray-500">(Click to view details)</span>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Build Statistics */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Build Statistics</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-sm text-gray-600">Execution Time</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{formatExecutionTime(dataBuild.executionTime)}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-gray-600">Created</span>
                </div>
                <span className="text-sm text-gray-900">{formatDate(dataBuild.createdAt)}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="text-sm text-gray-600">Updated</span>
                </div>
                <span className="text-sm text-gray-900">{formatDate(dataBuild.updatedAt)}</span>
              </div>
              
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm text-gray-600">Source</span>
                  </div>
                  <a 
                    href={`https://docs.google.com/spreadsheets/d/${dataBuild.spreadsheet}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-mono truncate max-w-32"
                    title={dataBuild.spreadsheet}
                  >
                    {dataBuild.spreadsheet}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bundles Section - Moved to top for importance */}
      {dataBuild.bundles && (
        <BundlesSection 
          buildId={dataBuild._id} 
          bundles={dataBuild.bundles}
          jsonBundles={dataBuild.jsonBundles}
          buildFormats={dataBuild.buildFormats}
        />
      )}

      {/* Logs Card */}
      {dataBuild.logs && dataBuild.logs.length > 0 && (
        <LogViewer logs={dataBuild.logs as LogEntry[]} title="Execution Logs" />
      )}

      {/* Modal context handles the publish modal now */}
    </div>
  );
};

export default DataBuildDetailsPage;
